import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useDownloadFlowStore } from '@/stores/useDownloadFlowStore';
import { toast } from 'sonner';
import AuthSignIn from './AuthSignIn';
import AuthErrorHandler, { AuthError } from './AuthErrorHandler';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  title?: string;
  description?: string;
  // Download flow specific props
  sequenceId?: string;
  sequenceTitle?: string;
  sequenceArtist?: string;
  isFree?: boolean;
  price?: number;
  isOwner?: boolean;
  albumCoverUrl?: string;
}

const AuthModal: React.FC<AuthModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  title = 'Sign in required',
  description = 'Please sign in to continue',
  // Download flow props
  sequenceId,
  sequenceTitle,
  sequenceArtist,
  isFree,
  price,
  isOwner,
  albumCoverUrl,
}) => {
  const { user, isAuthenticated } = useAuth();
  const {
    startDownloadSession,
    hideAuthModal,
    currentSession,
    getNextModalInFlow,
    showDonationModal,
    showAddedToDisplayModal,
    showConfirmationPage,
    endDownloadSession,
  } = useDownloadFlowStore();

  const [authError, setAuthError] = useState<AuthError | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isModalClosing, setIsModalClosing] = useState(false);

  // Handle successful authentication
  const handleAuthSuccess = () => {
    try {
      // Clear any existing errors
      setAuthError(null);
      setRetryCount(0);

      // If we have download flow context, start or resume the session
      if (sequenceId && sequenceTitle && sequenceArtist) {
        startDownloadSession({
          sequenceId,
          sequenceTitle,
          sequenceArtist,
          isOwner: isOwner || false,
          isFree: isFree || false,
          price,
          userId: user?.id,
        });
      } else if (currentSession) {
        // Resume existing session with user info
        startDownloadSession({
          ...currentSession,
          userId: user?.id,
        });
      }

      // Hide auth modal
      hideAuthModal();
      
      // Determine next step in download flow
      const nextModal = getNextModalInFlow();
      
      switch (nextModal) {
        case 'donation':
          showDonationModal();
          break;
        case 'addedToDisplay':
          showAddedToDisplayModal();
          break;
        case 'confirmation':
          showConfirmationPage();
          break;
        default:
          // Call original onSuccess if provided
          onSuccess?.();
          break;
      }

      onClose();
    } catch (error) {
      console.error('Error in authentication success flow:', error);
      handleAuthError('unknown', error instanceof Error ? error.message : 'Failed to continue after authentication');
    }
  };

  // Handle authentication errors
  const handleAuthError = (errorType: AuthError['type'], message: string) => {
    const newRetryCount = retryCount + 1;
    setRetryCount(newRetryCount);
    setAuthError({
      type: errorType,
      message,
      canRetry: newRetryCount < 3 && errorType !== 'user_cancelled',
      retryCount: newRetryCount,
    });

    toast.error('Authentication Error', {
      description: message,
    });
  };

  // Handle modal cancellation/closure
  const handleModalClose = (reason: 'user_action' | 'escape' | 'outside_click' = 'user_action') => {
    setIsModalClosing(true);
    
    // If there's an active download session and user is cancelling, handle it properly
    if (currentSession && reason === 'user_action') {
      const hasDownloadContext = sequenceId || currentSession.sequenceId;
      
      if (hasDownloadContext) {
        handleAuthError('user_cancelled', 'Authentication was cancelled. You can try again or continue as a guest if available.');
        endDownloadSession();
      }
    }

    // Clear errors when closing
    setAuthError(null);
    setRetryCount(0);
    
    hideAuthModal();
    onClose();
    setIsModalClosing(false);
  };

  // Handle retry authentication
  const handleRetryAuth = () => {
    setAuthError(null);
    // Reset to fresh auth state - the user will need to input credentials again
  };

  // Handle auth error cancellation
  const handleErrorCancel = () => {
    setAuthError(null);
    setRetryCount(0);
    handleModalClose('user_action');
  };

  // Auto-close modal if user becomes authenticated
  useEffect(() => {
    if (isAuthenticated && isOpen) {
      handleAuthSuccess();
    }
  }, [isAuthenticated, isOpen]);

  // Monitor for authentication errors by tracking authentication state transitions
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    if (isOpen && !isAuthenticated) {
      // Check for network connectivity
      const checkConnection = async () => {
        try {
          await fetch('/api/health', { method: 'HEAD', cache: 'no-cache' });
        } catch (error) {
          if (navigator.onLine === false) {
            handleAuthError('network_error', 'No internet connection detected. Please check your network settings.');
          }
        }
      };

      // Check connection after a delay if still not authenticated
      timeoutId = setTimeout(checkConnection, 5000);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isOpen, isAuthenticated]);

  // Listen for session timeout via download flow session expiration
  useEffect(() => {
    if (currentSession && isOpen) {
      const sessionTimeout = currentSession.expiresAt.getTime() - Date.now();
      
      if (sessionTimeout <= 0) {
        handleAuthError('session_timeout', 'Your session has expired. Please sign in again.');
        return;
      }

      const timeoutId = setTimeout(() => {
        handleAuthError('session_timeout', 'Your session has expired for security reasons.');
      }, sessionTimeout);

      return () => clearTimeout(timeoutId);
    }
  }, [currentSession, isOpen]);

  // Generate dynamic title and description based on download context
  const getModalContent = () => {
    if (sequenceId && sequenceTitle && sequenceArtist) {
      if (isFree) {
        return {
          title: 'Sign in to download',
          description: 'Your download will be saved to your account.',
        };
      } else {
        return {
          title: 'Sign in to purchase and download',
          description: 'Your purchase will be saved to your account.',
        };
      }
    }
    
    return { title, description };
  };

  const { title: modalTitle, description: modalDescription } = getModalContent();

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleModalClose('user_action')}>
      <DialogContent className="sm:max-w-[425px]">
        {authError ? (
          <AuthErrorHandler
            error={authError}
            onRetry={handleRetryAuth}
            onCancel={handleErrorCancel}
            isVisible={true}
          />
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{modalTitle}</DialogTitle>
              <DialogDescription className="text-left">
                {modalDescription}
              </DialogDescription>
            </DialogHeader>
        
        {/* Show compact sequence info (album art + title only) if available */}
        {sequenceId && sequenceTitle && (
          <div className="bg-muted/50 rounded-lg p-3 border">
            <div className="flex items-center gap-3">
              {albumCoverUrl && (
                <img
                  src={albumCoverUrl}
                  alt="Album cover"
                  className="h-10 w-10 rounded object-cover"
                />
              )}
              <div className="min-w-0">
                <h4 className="font-medium text-sm truncate">{sequenceTitle}</h4>
              </div>
            </div>
          </div>
        )}

            <AuthSignIn 
              onViewChange={() => {}} 
              onSuccess={handleAuthSuccess}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal; 